// tab/src/components/ClockOnOff/ClockOnOff.tsx
// OnRounds Phase 2 - Clock On/Off Component

import React, { useState, useEffect, useCallback } from 'react';
import {
  makeStyles,
  shorthands,
  tokens,
  Card,
  CardHeader,
  Text,
  Button,
  Dropdown,
  Option,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Field,
  Textarea,
  Badge,
  Spinner,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
} from '@fluentui/react-components';
import {
  Clock24Regular,
  Clock24Filled,
  ArrowExit24Regular,
  Person24Regular,
  Mail24Regular,
  Warning24Regular,
  Checkmark24Regular,
} from '@fluentui/react-icons';
import { format, formatDistanceToNow } from 'date-fns';
import { useUser } from '../../context/UserContext';
import { useTeam } from '../../context/TeamContext';
import { api } from '../../services/api';
import { Loading } from '../common/Loading';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('24px'),
    maxWidth: '800px',
    ...shorthands.margin('0', 'auto'),
  },
  statusCard: {
    ...shorthands.padding('24px'),
  },
  statusHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '16px',
  },
  statusInfo: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('12px'),
  },
  statusBadge: {
    ...shorthands.padding('8px', '16px'),
    ...shorthands.borderRadius('8px'),
    backgroundColor: tokens.colorPaletteGreenBackground2,
    color: tokens.colorPaletteGreenForeground1,
    fontWeight: tokens.fontWeightSemibold,
  },
  statusBadgeOff: {
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground2,
  },
  shiftDetails: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    ...shorthands.gap('16px'),
    marginTop: '16px',
    ...shorthands.padding('16px'),
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.borderRadius('8px'),
  },
  detailItem: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('4px'),
  },
  detailLabel: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  detailValue: {
    fontWeight: tokens.fontWeightSemibold,
  },
  roleGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
    ...shorthands.gap('16px'),
  },
  roleCard: {
    cursor: 'pointer',
    transitionProperty: 'all',
    transitionDuration: '0.2s',
    ':hover': {
      boxShadow: tokens.shadow8,
    },
  },
  roleCardDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  roleHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  roleStatus: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('8px'),
    marginTop: '8px',
  },
  pendingMessages: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('12px'),
    marginTop: '16px',
    ...shorthands.padding('16px'),
    backgroundColor: tokens.colorPaletteYellowBackground2,
    ...shorthands.borderRadius('8px'),
  },
  messageItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.padding('8px'),
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('4px'),
  },
  priorityBadge: {
    ...shorthands.padding('2px', '8px'),
    ...shorthands.borderRadius('4px'),
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
  },
  priorityCritical: {
    backgroundColor: tokens.colorPaletteRedBackground2,
    color: tokens.colorPaletteRedForeground1,
  },
  priorityUrgent: {
    backgroundColor: tokens.colorPaletteYellowBackground2,
    color: tokens.colorPaletteYellowForeground2,
  },
  priorityRoutine: {
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground2,
  },
});

interface Role {
  id: string;
  name: string;
  description: string;
  category: string;
  color: string;
  status?: 'vacant' | 'occupied' | 'handover_pending';
  currentHolder?: string;
  unreadCount?: number;
}

interface PendingMessage {
  id: string;
  subject: string;
  fromUserName: string;
  priority: 'routine' | 'urgent' | 'critical';
  createdAt: string;
}

export const ClockOnOff: React.FC = () => {
  const styles = useStyles();
  const { user, currentShift, refreshShift, setCurrentShift } = useUser();
  const { currentTeam } = useTeam();

  const [roles, setRoles] = useState<Role[]>([]);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [pendingMessages, setPendingMessages] = useState<PendingMessage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isClockingOn, setIsClockingOn] = useState(false);
  const [isClockingOff, setIsClockingOff] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [clockOnDialogOpen, setClockOnDialogOpen] = useState(false);
  const [clockOffDialogOpen, setClockOffDialogOpen] = useState(false);
  const [clockOffNotes, setClockOffNotes] = useState('');

  // Load roles and current shift status
  const loadData = useCallback(async () => {
    alert(`1. loadData called. currentTeam: ${currentTeam ? currentTeam.id : 'NULL'}`);
    
    if (!currentTeam) {
      setIsLoading(false);
      return;
    }
  
    try {
      setIsLoading(true);
      setError(null);
  
      alert(`2. Fetching roles for team: ${currentTeam.id}`);
      
      // Get role directory
      const directory = await api.getRoleDirectory(currentTeam.id);
      
      alert(`3. Got ${directory.roles.length} roles`);
      
      const mappedRoles: Role[] = directory.roles.map((r) => ({
        id: r.roleId,
        name: r.roleName,
        description: r.role?.description || '',
        category: r.role?.category || 'other',
        color: r.role?.color || '#6B7280',
        status: r.status,
        currentHolder: r.userDisplayName || undefined,
        unreadCount: r.unreadMessageCount || 0,
      }));
  
      setRoles(mappedRoles);
  
      // Refresh current shift
      await refreshShift(currentTeam.id);
    } catch (err: any) {
      alert(`ERROR: ${err.message}`);
      console.error('Failed to load data:', err);
      setError(err.message || 'Failed to load data');
    } finally {
      setIsLoading(false);
    }
  }, [currentTeam, refreshShift]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handle clock on
  const handleClockOn = async () => {
    if (!selectedRole || !currentTeam) return;

    try {
      setIsClockingOn(true);
      setError(null);

      const result = await api.clockOn({
        teamId: currentTeam.id,
        roleId: selectedRole.id,
        userId: user?.teamsUserId,
        userDisplayName: user?.displayName,
      });

      // Update state
      setCurrentShift({
        id: result.shift.id,
        teamId: result.shift.teamId,
        roleId: result.shift.roleId,
        roleName: selectedRole.name,
        clockOnTime: result.shift.clockOnTime,
        plannedEndTime: result.shift.plannedEndTime,
      });

      // Store any pending messages for display
      if (result.pendingMessages && result.pendingMessages.length > 0) {
        setPendingMessages(result.pendingMessages);
      }

      setClockOnDialogOpen(false);
      setSelectedRole(null);
      
      // Refresh data
      loadData();
    } catch (err: any) {
      console.error('Failed to clock on:', err);
      setError(err.message || 'Failed to clock on');
    } finally {
      setIsClockingOn(false);
    }
  };

  // Handle clock off
  const handleClockOff = async () => {
    if (!currentShift || !currentTeam) return;

    try {
      setIsClockingOff(true);
      setError(null);

      const result = await api.clockOff({
        shiftId: currentShift.id,
        notes: clockOffNotes,
      });

      // Clear shift
      setCurrentShift(null);
      setPendingMessages([]);
      setClockOffDialogOpen(false);
      setClockOffNotes('');

      // Show warning if there are unhandled messages
      if (result.handoverRequired && result.unhandledMessages.length > 0) {
        setError(`Warning: ${result.unhandledMessages.length} messages were not addressed and will be passed to the next person.`);
      }

      // Refresh data
      loadData();
    } catch (err: any) {
      console.error('Failed to clock off:', err);
      setError(err.message || 'Failed to clock off');
    } finally {
      setIsClockingOff(false);
    }
  };

  // Handle role selection
  const handleRoleSelect = (role: Role) => {
    if (role.status === 'occupied' && role.currentHolder !== user?.displayName) {
      // Role is occupied by someone else
      return;
    }
    setSelectedRole(role);
    setClockOnDialogOpen(true);
  };

  // Check if user can select a role
  const canSelectRole = (role: Role): boolean => {
    if (currentShift) return false; // Already clocked on
    if (role.status === 'occupied') return false; // Role taken
    return true;
  };

  if (isLoading) {
    return <Loading message="Loading shift information..." />;
  }

  return (
    <div className={styles.container}>
      {/* Error Message */}
      {error && (
        <MessageBar intent="error">
          <MessageBarBody>
            <MessageBarTitle>Error</MessageBarTitle>
            {error}
          </MessageBarBody>
        </MessageBar>
      )}

      {/* Current Shift Status */}
      <Card className={styles.statusCard}>
        <div className={styles.statusHeader}>
          <div className={styles.statusInfo}>
            {currentShift ? (
              <Clock24Filled style={{ color: tokens.colorPaletteGreenForeground1 }} />
            ) : (
              <Clock24Regular />
            )}
            <Text size={500} weight="semibold">
              {currentShift ? 'Currently On Shift' : 'Not Clocked On'}
            </Text>
          </div>
          
          <span className={`${styles.statusBadge} ${!currentShift ? styles.statusBadgeOff : ''}`}>
            {currentShift ? 'Active' : 'Off Duty'}
          </span>
        </div>

        {currentShift && (
          <>
            <div className={styles.shiftDetails}>
              <div className={styles.detailItem}>
                <Text className={styles.detailLabel}>Role</Text>
                <Text className={styles.detailValue}>{currentShift.roleName}</Text>
              </div>
              <div className={styles.detailItem}>
                <Text className={styles.detailLabel}>Started</Text>
                <Text className={styles.detailValue}>
                  {format(new Date(currentShift.clockOnTime), 'h:mm a')}
                </Text>
              </div>
              <div className={styles.detailItem}>
                <Text className={styles.detailLabel}>Duration</Text>
                <Text className={styles.detailValue}>
                  {formatDistanceToNow(new Date(currentShift.clockOnTime))}
                </Text>
              </div>
              {currentShift.plannedEndTime && (
                <div className={styles.detailItem}>
                  <Text className={styles.detailLabel}>Planned End</Text>
                  <Text className={styles.detailValue}>
                    {format(new Date(currentShift.plannedEndTime), 'h:mm a')}
                  </Text>
                </div>
              )}
            </div>

            {/* Pending Messages Alert */}
            {pendingMessages.length > 0 && (
              <div className={styles.pendingMessages}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Warning24Regular />
                  <Text weight="semibold">
                    {pendingMessages.length} message(s) handed over to you
                  </Text>
                </div>
                {pendingMessages.slice(0, 3).map((msg) => (
                  <div key={msg.id} className={styles.messageItem}>
                    <div>
                      <Text weight="semibold">{msg.subject}</Text>
                      <Text size={200}> from {msg.fromUserName}</Text>
                    </div>
                    <span className={`${styles.priorityBadge} ${
                      msg.priority === 'critical' ? styles.priorityCritical :
                      msg.priority === 'urgent' ? styles.priorityUrgent :
                      styles.priorityRoutine
                    }`}>
                      {msg.priority}
                    </span>
                  </div>
                ))}
                {pendingMessages.length > 3 && (
                  <Text size={200}>
                    And {pendingMessages.length - 3} more...
                  </Text>
                )}
              </div>
            )}

            {/* Clock Off Button */}
            <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                appearance="secondary"
                icon={<ArrowExit24Regular />}
                onClick={() => setClockOffDialogOpen(true)}
              >
                Clock Off
              </Button>
            </div>
          </>
        )}

        {!currentShift && (
          <Text style={{ marginTop: '8px', color: tokens.colorNeutralForeground3 }}>
            Select a role below to clock on and start your shift.
          </Text>
        )}
      </Card>

      {/* Available Roles */}
      {!currentShift && (
        <>
          <Text size={400} weight="semibold">Available Roles</Text>
          <div className={styles.roleGrid}>
            {roles.map((role) => (
              <Card
                key={role.id}
                className={`${styles.roleCard} ${!canSelectRole(role) ? styles.roleCardDisabled : ''}`}
                onClick={() => canSelectRole(role) && handleRoleSelect(role)}
              >
                <CardHeader
                  header={
                    <div className={styles.roleHeader}>
                      <Text weight="semibold">{role.name}</Text>
                      <span
                        style={{
                          width: '12px',
                          height: '12px',
                          borderRadius: '50%',
                          backgroundColor: role.color,
                        }}
                      />
                    </div>
                  }
                  description={role.description || role.category}
                />
                <div className={styles.roleStatus}>
                  {role.status === 'occupied' ? (
                    <>
                      <Person24Regular />
                      <Text size={200}>{role.currentHolder}</Text>
                    </>
                  ) : role.status === 'handover_pending' ? (
                    <>
                      <Warning24Regular style={{ color: tokens.colorPaletteYellowForeground2 }} />
                      <Text size={200}>Handover Pending</Text>
                    </>
                  ) : (
                    <>
                      <Checkmark24Regular style={{ color: tokens.colorPaletteGreenForeground1 }} />
                      <Text size={200}>Available</Text>
                    </>
                  )}
                  {role.unreadCount && role.unreadCount > 0 && (
                    <Badge appearance="filled" color="danger" size="small">
                      {role.unreadCount} msg
                    </Badge>
                  )}
                </div>
              </Card>
            ))}
          </div>
        </>
      )}

      {/* Clock On Dialog */}
      <Dialog open={clockOnDialogOpen} onOpenChange={(e, data) => setClockOnDialogOpen(data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Clock On to {selectedRole?.name}</DialogTitle>
            <DialogContent>
              <Text>
                You are about to clock on as <strong>{selectedRole?.name}</strong>.
                Any messages sent to this role will be delivered to you.
              </Text>
              {selectedRole?.unreadCount && selectedRole.unreadCount > 0 && (
                <MessageBar intent="warning" style={{ marginTop: '16px' }}>
                  <MessageBarBody>
                    There are {selectedRole.unreadCount} pending message(s) for this role that will be handed over to you.
                  </MessageBarBody>
                </MessageBar>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setClockOnDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={handleClockOn}
                disabled={isClockingOn}
                icon={isClockingOn ? <Spinner size="tiny" /> : <Clock24Regular />}
              >
                {isClockingOn ? 'Clocking On...' : 'Clock On'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Clock Off Dialog */}
      <Dialog open={clockOffDialogOpen} onOpenChange={(e, data) => setClockOffDialogOpen(data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Clock Off</DialogTitle>
            <DialogContent>
              <Text>
                You are about to clock off from <strong>{currentShift?.roleName}</strong>.
              </Text>
              <Field label="Notes (optional)" style={{ marginTop: '16px' }}>
                <Textarea
                  value={clockOffNotes}
                  onChange={(e, data) => setClockOffNotes(data.value)}
                  placeholder="Any notes for handover..."
                  rows={3}
                />
              </Field>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setClockOffDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={handleClockOff}
                disabled={isClockingOff}
                icon={isClockingOff ? <Spinner size="tiny" /> : <ArrowExit24Regular />}
              >
                {isClockingOff ? 'Clocking Off...' : 'Clock Off'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};
